import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface Story {
  id: string;
  slug: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  excerpt_en: string;
  excerpt_ps: string;
  excerpt_da: string;
  content_en: string;
  content_ps: string;
  content_da: string;
  author_en: string;
  author_ps: string;
  author_da: string;
  author_role_en: string;
  author_role_ps: string;
  author_role_da: string;
  image: string;
  category: string;
  read_time_en: string;
  read_time_ps: string;
  read_time_da: string;
  published: boolean;
}

interface Props {
  story: Story | null;
  onClose: () => void;
}

const CATEGORIES = ['Field Stories', 'Impact Reports', 'Volunteer Voices', 'News & Updates'];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .substring(0, 120);
}

export default function StoryForm({ story, onClose }: Props) {
  const isEdit = !!story;
  const [form, setForm] = useState({
    title_en: '',
    slug: '',
    excerpt_en: '',
    content_en: '',
    author_en: '',
    author_role_en: '',
    image: '',
    category: 'Field Stories',
    read_time_en: '',
    published: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (story) {
      setForm({
        title_en: story.title_en,
        slug: story.slug,
        excerpt_en: story.excerpt_en,
        content_en: story.content_en,
        author_en: story.author_en,
        author_role_en: story.author_role_en,
        image: story.image,
        category: story.category,
        read_time_en: story.read_time_en,
        published: story.published,
      });
    }
  }, [story]);

  const handleChange = (field: string, value: string | boolean) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'title_en' && !isEdit) {
        updated.slug = slugify(value as string);
      }
      return updated;
    });
  };

  const handleSave = async () => {
    if (!form.title_en.trim()) { setError('Title is required.'); return; }
    if (!form.slug.trim()) { setError('Slug is required.'); return; }
    setError('');
    setSaving(true);

    const payload = {
      slug: form.slug,
      title_en: form.title_en,
      excerpt_en: form.excerpt_en,
      content_en: form.content_en,
      author_en: form.author_en,
      author_role_en: form.author_role_en,
      image: form.image,
      category: form.category,
      read_time_en: form.read_time_en,
      published: form.published,
      updated_at: new Date().toISOString(),
    };

    if (isEdit) {
      const { error: updateErr } = await supabase.from('stories').update(payload).eq('id', story.id);
      if (updateErr) { setError(updateErr.message); setSaving(false); return; }
    } else {
      const { error: insertErr } = await supabase.from('stories').insert({ ...payload, created_at: new Date().toISOString() });
      if (insertErr) { setError(insertErr.message); setSaving(false); return; }
    }

    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 pb-12 overflow-y-auto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-2xl mx-4 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#1B2838]">
            {isEdit ? 'Edit Story' : 'New Story'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Form Body */}
        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-error-warning-line"></i></div>
              {error}
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title <span className="text-red-400">*</span></label>
            <input
              value={form.title_en}
              onChange={e => handleChange('title_en', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
              placeholder="Enter the story title in English..."
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">URL Slug <span className="text-red-400">*</span></label>
            <input
              value={form.slug}
              onChange={e => handleChange('slug', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] font-mono"
              placeholder="story-url-slug"
            />
          </div>

          {/* Category + Read Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
              <select
                value={form.category}
                onChange={e => handleChange('category', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] cursor-pointer"
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Read Time</label>
              <input
                value={form.read_time_en}
                onChange={e => handleChange('read_time_en', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                placeholder="e.g. 5 min read"
              />
            </div>
          </div>

          {/* Author + Role */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Author Name</label>
              <input
                value={form.author_en}
                onChange={e => handleChange('author_en', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                placeholder="Author name"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Author Role</label>
              <input
                value={form.author_role_en}
                onChange={e => handleChange('author_role_en', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
                placeholder="e.g. Communications Lead, CAF"
              />
            </div>
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Image URL</label>
            <input
              value={form.image}
              onChange={e => handleChange('image', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4]"
              placeholder="https://..."
            />
            {form.image && (
              <div className="mt-2 rounded-lg overflow-hidden bg-gray-100 h-40">
                <img src={form.image} alt="Preview" className="w-full h-full object-cover object-top" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
            )}
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Excerpt</label>
            <textarea
              value={form.excerpt_en}
              onChange={e => handleChange('excerpt_en', e.target.value)}
              rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none"
              placeholder="Short summary shown in story cards and previews..."
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Content (HTML)</label>
            <textarea
              value={form.content_en}
              onChange={e => handleChange('content_en', e.target.value)}
              rows={10}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#4ECDC4] resize-none font-mono"
              placeholder="<p>Write the full story content with HTML tags...</p>"
            />
          </div>

          {/* Published toggle */}
          <div className="flex items-center gap-3 pt-2">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={e => handleChange('published', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#4ECDC4]"></div>
            </label>
            <span className="text-sm text-gray-600">{form.published ? 'Published — visible on the website' : 'Draft — hidden from visitors'}</span>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex items-start gap-2">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-information-line"></i></div>
            <span>Pashto and Dari translations are managed separately in the CMS section — use the Pashto CMS and Dari CMS pages to translate this story after saving.</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-600 text-sm font-medium cursor-pointer hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-[#1B2838] text-white rounded-full text-sm font-bold cursor-pointer hover:bg-[#2a3a4a] disabled:opacity-60 whitespace-nowrap flex items-center gap-2"
          >
            {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
            {isEdit ? 'Save Changes' : 'Create Story'}
          </button>
        </div>
      </div>
    </div>
  );
}